import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom'; // 1. Importamos Hooks de Router
import { Menu, X, Calculator, Phone, Mail, ChevronDown, LogIn } from 'lucide-react';

const Navbar = ({ scrolled, language, toggleLanguage }) => {
  // Nota: Ya no recibimos navigateTo ni currentPage, lo manejamos internamente
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);

  // 2. Hook para saber la URL actual
  const location = useLocation();

  // Helper: Verifica si la ruta coincide para resaltar el botón
  const isActive = (path) => location.pathname === path;

  // --- DICCIONARIO DE TEXTOS (Sin cambios) ---
  const t = {
    es: {
      ready: '¡Estamos listos para ayudarte!',
      home: 'Inicio',
      services: 'Servicios',
      work: 'Trabaja con Nosotros',
      blog: 'Blog',
      contact: 'Contacto',
      quote: 'Cotizar',
      quoteMobile: 'Cotizar Ahora',
      buk: 'Portal Empleados',
      viewAll: 'Ver todos los servicios',
      needHelp: '¿Necesitas ayuda inmediata?',
      sst: 'Seguridad y Salud (SST)',
      rrhh: 'Outsourcing RR.HH.',
      seleccion: 'Selección de Personal',
      nomina: 'Outsourcing Nómina',
      estructura: 'Estructura Organizacional',
      independientes: 'Servicios Independientes'
    },
    en: {
      ready: 'We are ready to help you!',
      home: 'Home',
      services: 'Services',
      work: 'Work with Us',
      blog: 'Blog',
      contact: 'Contact',
      quote: 'Get Quote',
      quoteMobile: 'Get Quote Now',
      buk: 'Employee Portal',
      viewAll: 'View all services',
      needHelp: 'Need immediate help?',
      sst: 'Health & Safety (SST)',
      rrhh: 'HR Outsourcing',
      seleccion: 'Recruitment',
      nomina: 'Payroll Outsourcing',
      estructura: 'Organizational Structure',
      independientes: 'Independent Services'
    }
  };

  const text = t[language] || t.es;

  const SERVICES_SUBMENU = [
    { label: text.sst, id: 'sst' },
    { label: text.rrhh, id: 'recursos-humanos' },
    { label: text.seleccion, id: 'seleccion-personal' },
    { label: text.nomina, id: 'nomina' },
    { label: text.estructura, id: 'estructura-organizacional' },
    { label: text.independientes, id: 'servicios-independientes' },
  ];

  // Helper para cerrar menú al hacer clic en un Link
  const closeMenu = () => setIsMenuOpen(false);

  // Helper para detectar si algún servicio está activo (para resaltar el padre)
  const isServicesActive = () => 
    location.pathname === '/servicios' || SERVICES_SUBMENU.some(s => location.pathname === `/${s.id}`);

  // Componente de Botón de Idioma
  const LanguageButton = ({ langCode, label, active }) => (
    <button 
      onClick={() => language !== langCode && toggleLanguage()}
      className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition-all duration-300 ${
        active 
          ? 'bg-white border-white text-purple-900 shadow-md scale-110' 
          : 'bg-transparent border-white/50 text-white/80 hover:bg-white hover:text-purple-900 hover:scale-105'
      }`}
      title={label}
    >
      {langCode.toUpperCase()}
    </button>
  );

  return (
    <>
      {/* --- BARRA SUPERIOR (TOP BAR) --- */}
      <div className="fixed w-full z-[60] top-0 bg-gradient-to-r from-purple-900 via-purple-800 to-indigo-900 text-white text-xs sm:text-sm font-medium py-2 shadow-md h-9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-full">
            <div className="hidden sm:flex items-center gap-2 animate-pulse">
               <span className="w-2 h-2 bg-green-400 rounded-full"></span>
               <span>{text.ready}</span>
            </div>
            <div className="flex items-center gap-4 sm:gap-6 w-full sm:w-auto justify-center sm:justify-end">
                <a href="tel:3114044203" className="flex items-center gap-2 hover:text-cyan-300 transition-colors">
                    <Phone size={14} className="text-cyan-400" />
                    <span>311 404 4203</span>
                </a>
                <a href="mailto:comercial@zaphy.com.co" className="flex items-center gap-2 hover:text-cyan-300 transition-colors">
                    <Mail size={14} className="text-cyan-400" />
                    <span className="truncate max-w-[150px] sm:max-w-none">comercial@zaphy.com.co</span>
                </a>
                <div className="hidden sm:flex items-center gap-2 ml-4 pl-4 border-l border-white/20">
                    <LanguageButton langCode="es" label="Español" active={language === 'es'} />
                    <LanguageButton langCode="en" label="English" active={language === 'en'} />
                </div>
            </div>
        </div>
      </div>

      {/* --- NAV BAR --- */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-2 top-9' : 'bg-transparent py-4 top-9'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            
            {/* LOGO (Ahora es un Link) */}
            <Link to="/" onClick={closeMenu} className="flex-shrink-0 flex items-center cursor-pointer group">
                <div className="flex items-center">
                    <img 
                        src="https://zaphy.com.co/wp-content/uploads/2021/07/cropped-LOGO-ZAPHY.png" 
                        alt="Zaphy Gestión Integral" 
                        className="h-20 w-auto object-contain transition-transform group-hover:scale-105" 
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                </div>
            </Link>

            {/* MENU ESCRITORIO */}
            <div className="hidden lg:flex items-center space-x-1">
              
              <Link 
                to="/" 
                className={`px-3 py-2 rounded-full font-medium transition-all font-[Poppins] text-sm capitalize ${isActive('/') ? 'text-purple-800 bg-purple-50' : 'text-gray-600 hover:text-purple-600'}`}
              >
                {text.home}
              </Link>

              {/* DROPDOWN DE SERVICIOS */}
              <div className="relative group">
                  <Link 
                    to="/servicios"
                    className={`px-3 py-2 rounded-full font-medium transition-all font-[Poppins] text-sm capitalize flex items-center gap-1 ${isServicesActive() ? 'text-purple-800 bg-purple-50' : 'text-gray-600 hover:text-purple-600'}`}
                  >
                    {text.services} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-300"/>
                  </Link>

                  <div className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform group-hover:translate-y-0 translate-y-2 z-50 overflow-hidden">
                      <div className="py-2">
                          {SERVICES_SUBMENU.map((service) => (
                              <Link
                                  key={service.id}
                                  to={`/${service.id}`}
                                  className="block w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors font-[Poppins] border-b border-gray-50 last:border-0"
                              >
                                  {service.label}
                              </Link>
                          ))}
                          <div className="border-t border-gray-100 mt-1 pt-1">
                             <Link 
                                to="/servicios"
                                className="block w-full text-left px-4 py-2 text-xs font-bold text-purple-600 hover:bg-purple-50 uppercase tracking-wider"
                             >
                                {text.viewAll} &rarr;
                             </Link>
                          </div>
                      </div>
                  </div>
              </div>

              {/* OTROS LINKS */}
              {[
                { id: 'trabaja-con-nosotros', label: text.work },
                { id: 'blog', label: text.blog },
                { id: 'contacto', label: text.contact }
              ].map((item) => (
                <Link 
                  key={item.id}
                  to={`/${item.id}`} 
                  className={`px-3 py-2 rounded-full font-medium transition-all font-[Poppins] text-sm capitalize ${isActive(`/${item.id}`) ? 'text-purple-800 bg-purple-50' : 'text-gray-600 hover:text-purple-600'}`}
                >
                  {item.label}
                </Link>
              ))}

              {/* BUK */}
              <a 
                href="https://zaphy.buk.co/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-2 px-4 py-2 rounded-full font-bold transition-all duration-300 font-[Poppins] text-sm text-purple-800 bg-white border-2 border-purple-800 hover:bg-purple-800 hover:text-white shadow-md hover:shadow-lg flex items-center gap-2 transform hover:-translate-y-0.5"
                title={text.buk}
              >
                  <LogIn size={16} /> 
                  <span className="hidden xl:inline">{text.buk}</span>
                  <span className="xl:hidden">Portal</span>
              </a>
              
              {/* COTIZADOR */}
              <Link to="/cotizador" className="ml-2 bg-purple-800 text-white px-5 py-2 rounded-full font-bold hover:bg-purple-900 transition-all shadow-lg hover:shadow-purple-500/30 transform hover:-translate-y-0.5 flex items-center gap-2 font-[Poppins] text-sm">
                <Calculator size={14} /> {text.quote}
              </Link>
            </div>

            {/* MENU MOVIL TOGGLE */}
            <button className="lg:hidden p-2 text-gray-600 hover:text-purple-800 transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={28}/> : <Menu size={28}/>}
            </button>
          </div>
        </div>

        {/* PANEL MÓVIL */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white border-t border-gray-100 shadow-xl h-[calc(100vh-60px)] overflow-y-auto p-6 space-y-2 animate-[fadeInDown_0.3s_ease-out]">
              
              <Link to="/" onClick={closeMenu} className="block w-full text-left p-4 text-lg font-bold border-b border-gray-50 text-gray-800 font-[Poppins]">
                {text.home}
              </Link>

              {/* ACORDEÓN MÓVIL */}
              <div className="border-b border-gray-50">
                  <button 
                    onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
                    className="w-full text-left p-4 text-lg font-bold text-gray-800 font-[Poppins] flex justify-between items-center"
                  >
                    {text.services} <ChevronDown size={20} className={`transition-transform duration-300 ${isMobileServicesOpen ? 'rotate-180' : ''}`}/>
                  </button>
                  
                  {isMobileServicesOpen && (
                      <div className="bg-gray-50 px-4 py-2 rounded-xl mb-4 space-y-1 animate-[fadeIn_0.3s_ease-out]">
                          {SERVICES_SUBMENU.map((service) => (
                              <Link
                                  key={service.id}
                                  to={`/${service.id}`}
                                  onClick={closeMenu}
                                  className="block w-full text-left p-3 text-sm text-gray-600 hover:text-purple-700 font-[Poppins] border-l-2 border-gray-200 hover:border-purple-50 pl-4"
                              >
                                  {service.label}
                              </Link>
                          ))}
                          <Link 
                             to="/servicios"
                             onClick={closeMenu}
                             className="block w-full text-left p-3 text-sm font-bold text-purple-700 underline"
                          >
                             {text.viewAll}
                          </Link>
                      </div>
                  )}
              </div>

              {/* OTROS LINKS MÓVIL */}
              {[
                { id: 'trabaja-con-nosotros', label: text.work },
                { id: 'blog', label: text.blog },
                { id: 'contacto', label: text.contact }
              ].map((item) => (
                <Link key={item.id} to={`/${item.id}`} onClick={closeMenu} className="block w-full text-left p-4 text-lg font-bold border-b border-gray-50 text-gray-800 font-[Poppins] capitalize">
                  {item.label}
                </Link>
              ))}

              <a 
                href="https://zaphy.buk.co/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block w-full text-left p-4 text-lg font-bold border-b border-gray-50 text-purple-800 font-[Poppins] flex items-center gap-2 hover:bg-purple-50"
              >
                  <LogIn size={18} /> {text.buk}
              </a>
              
              <Link to="/cotizador" onClick={closeMenu} className="block w-full text-left p-4 text-lg font-bold border-b border-gray-50 text-purple-700 font-[Poppins] flex items-center gap-2 mt-4 bg-purple-50 rounded-xl">
                  <Calculator size={18} /> {text.quoteMobile}
              </Link>

              {/* SELECTOR IDIOMA MÓVIL */}
              <div className="mt-8 pt-8 border-t border-gray-100 flex justify-center gap-4">
                  <button 
                      onClick={() => language === 'en' && toggleLanguage()}
                      className={`px-6 py-2 rounded-full border-2 text-sm font-bold transition-all ${language === 'es' ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-300 text-gray-500'}`}
                  >
                      Español
                  </button>
                  <button 
                      onClick={() => language === 'es' && toggleLanguage()}
                      className={`px-6 py-2 rounded-full border-2 text-sm font-bold transition-all ${language === 'en' ? 'bg-purple-600 border-purple-600 text-white' : 'border-gray-300 text-gray-500'}`}
                  >
                      English
                  </button>
              </div>

              <div className="mt-4 text-center text-gray-500 text-sm">
                  <p>{text.needHelp}</p>
                  <a href="tel:3114044203" className="text-purple-600 font-bold text-lg mt-2 block">311 404 4203</a>
              </div>
          </div>
        )}
      </nav>

      {/* ESPACIADOR */}
      <div className="h-[10px] w-full bg-transparent"></div>
    </>
  );
};

export default Navbar;